import { MdLockOutline } from 'react-icons/md';
import { DimensionId, KpiId } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { MetricPickerChart } from '@Components/ChartRenderer';
import { Button } from '@Components/Button/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { FetchError } from '../../../utils/fetch-error';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { useChartEnvelope } from '../../Charts/hooks/useChartData';
import { useThisCard, useThisCardChartData } from '../CardChartContext';
import { getSavedKpiId, kpiOptionsFor, usesKpiPicker } from '../date-range';
import { useDashboardView } from '../DashboardViewContext';

interface ChartCardProps {
  // Offered when the chart behind this card is GONE: taking the card off the dashboard is the only
  // thing the user can actually do about it. Runs the same confirm the ⋮ menu does.
  onRemove?: () => void;
  removeDisabled?: boolean;
}

// A dashboard chart card: draws the card's chart (from its CardChartProvider, with any working copy
// as a preview) through the shared ChartRenderer, the same renderer the Dashboard Agent uses.
export const ChartCard = ({ onRemove, removeDisabled }: ChartCardProps) => {
  const { cid: company, cross_channel_kpis: crossChannelKpis } = useCompanyInfo();
  const {
    resolveKpi,
    setPerChartKpi,
    resolveMetric,
    setPerChartMetric,
    resolveBreakdown,
    setPerChartBreakdown
  } = useDashboardView();
  const { chartId } = useThisCard();
  const { data, isLoading, isPlaceholderData, isError, error } = useThisCardChartData();

  // THE ACCESS CHECK, and it deliberately does not come from the query above.
  //
  // `useChartData` SKIPS /getChart whenever it is given a previewConfig — the preview renders from
  // local state and goes straight to /getData, which is ungated by design because the caller
  // supplies the query. So a working copy left in sessionStorage would keep rendering a chart the
  // server has since stopped being willing to send, and the query above would never see the 403.
  //
  // The envelope query is the one that always asks the server. It shares a cache entry with the
  // call DashboardCard already makes for this card's title, so this costs no extra request.
  const { error: accessError } = useChartEnvelope(company, chartId);
  const isRestricted = accessError instanceof FetchError && accessError.status === 403;

  // Checked BEFORE isLoading and before the preview below: a restricted chart must not render, and
  // must not sit on a spinner either.
  if (isRestricted) return <RestrictedCard />;

  // isLoading: the wrapping ChartContainer already shows its own spinner (driven by the sibling
  // ChartLoadingTracker in DashboardCard.tsx) in place of this component, so there's nothing to render
  // here for a true first load. A background breakdown refetch instead falls through to the chart
  // below, keeping the previous render on screen (keepPreviousData) — isLoading is false by then.
  if (isLoading) return null;
  if (isError || !data) {
    const status = error instanceof FetchError ? error.status : undefined;

    // 403: the chart exists on this dashboard, but this viewer is not cleared for IT — while they
    // ARE cleared for the dashboard. A dashboard is governed by its own level and never by the
    // charts on it, so this is a routine state, not a fault, and the rest of the grid draws
    // normally around it.
    //
    // This branch must sit ABOVE the 404 branch and must NOT share its button. A user who cannot
    // see a chart must not be offered "Remove from dashboard".
    if (status === 403) return <RestrictedCard />;

    // A 404 means the chart itself is gone — deleted, or referenced by a dashboard copied from
    // somewhere its charts don't exist. That's a different problem from a request that failed, and
    // it's worth saying so: only one of the two is worth retrying, and only one has a remedy.
    const chartIsGone = status === 404;
    return (
      <div className='dashboard-card-failure'>
        <span>{chartIsGone ? 'This chart no longer exists.' : "Couldn't load this chart."}</span>
        {chartIsGone && onRemove && (
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            disabled={removeDisabled}
            onClick={onRemove}
          >
            Remove from dashboard
          </Button>
        )}
      </div>
    );
  }

  // Declaration-first KPI picker visibility: a declared `kpi` control shows the picker; absent one,
  // fall back to inferring it from any KPI-using query. Default to the override, else the saved KPI.
  const usesKpi = usesKpiPicker(data.config);
  const currentKpi = resolveKpi(chartId) ?? getSavedKpiId(data.config);
  const kpiPicker =
    usesKpi && currentKpi
      ? {
          options: kpiOptionsFor(crossChannelKpis, currentKpi),
          value: currentKpi,
          onChange: (kpiId: KpiId) => setPerChartKpi(chartId, kpiId)
        }
      : undefined;

  // The metric and breakdown picks are held in the dashboard's view context (`?? null` keeps both
  // pickers controlled while nothing is picked yet), so the card header can read what this chart is
  // showing when the user expands it. showTitle={false}: the card header owns the title (see
  // DashboardCard), so the chart body must not draw its own.
  return (
    <MetricPickerChart
      config={data.config}
      data={data.data}
      kpiPicker={kpiPicker}
      metric={resolveMetric(chartId) ?? null}
      onMetricChange={(colId: string) => setPerChartMetric(chartId, colId)}
      breakdown={resolveBreakdown(chartId) ?? null}
      onBreakdownChange={(dimId: DimensionId) => setPerChartBreakdown(chartId, dimId)}
      showTitle={false}
      isRefetching={isPlaceholderData}
    />
  );
};

/**
 * A chart on this dashboard that this viewer is not cleared to see.
 *
 * Drawn as a PLACEHOLDER, never by removing the card. `DashboardPlacement.position` is an absolute
 * {x, y} on a four-column grid and nothing compacts that grid at draw time — `autoCompact` runs only
 * when a card or section is explicitly removed. So a card dropped here leaves a hole that never
 * closes, and the dashboard reads as broken rather than as partly private.
 *
 * Deliberately quiet. The card states a RULE, not a fault: no alarm colour, no retry, no Remove, and
 * no other action. It is also left UNNAMED — DashboardCardTitle returns null without data and the
 * layout carries no title of its own, so the slot says "not for you" without disclosing what the
 * chart is called. Leave it that way.
 *
 * Exported only so the dashboarding-permissions story shows THIS component rather than a copy of
 * its markup — a copy would drift, and the whole point of that story is what these four states
 * actually look like. Nothing else imports it.
 */
export const RestrictedCard = () => (
  <div className='dashboard-card-failure'>
    <MdLockOutline size={24} />
    <span>You don&apos;t have access to this chart.</span>
  </div>
);
